/**
 * Selo de "novidade" no trilho: marca um painel recém-criado até a pessoa
 * abri-lo uma vez.
 *
 * Existe porque as camadas extras nascem desligadas — quem abre o AmaView vê
 * satélite e focos, e não tem como saber que existe uma aba nova com o nível
 * dos rios. A aba está lá, mas nada a distingue das que já estavam.
 *
 * O selo é discreto de propósito: um ponto de 6 px na quina do ícone, na cor
 * de acento do app. Ele não pisca, não cobre o rótulo e **some para sempre**
 * na primeira vez que a pessoa abre aquele painel — um aviso que insiste
 * depois de visto vira ruído.
 *
 * Não é o mesmo ponto do `useWarning`, que é amarelo e quer dizer "os dados
 * estão indisponíveis". Dois avisos da mesma cor no mesmo canto seriam
 * indistinguíveis; quando os dois valem ao mesmo tempo, o amarelo ganha,
 * porque problema no dado importa mais que novidade.
 */
import { useSyncExternalStore } from 'react';

const CHAVE = 'amaview.paineisVistos';

/** Quem já foi aberto, nesta máquina. */
function lerVistos(): Set<string> {
  try {
    const cru = localStorage.getItem(CHAVE);
    const j = cru ? JSON.parse(cru) : null;
    return new Set(Array.isArray(j) ? j.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    // Navegação privada, armazenamento bloqueado, JSON corrompido: o selo
    // aparece de novo, que é o pior caso aceitável.
    return new Set();
  }
}

let vistos = typeof localStorage === 'undefined' ? new Set<string>() : lerVistos();
const ouvintes = new Set<() => void>();

function assinar(fn: () => void): () => void {
  ouvintes.add(fn);
  return () => ouvintes.delete(fn);
}

/** Registra que a pessoa abriu este painel. Idempotente. */
export function marcarVisto(id: string): void {
  if (vistos.has(id)) return;
  vistos = new Set(vistos).add(id);
  try {
    localStorage.setItem(CHAVE, JSON.stringify([...vistos]));
  } catch {
    // Sem armazenamento, o selo volta na próxima visita. Não é motivo para
    // derrubar nada.
  }
  for (const fn of ouvintes) fn();
}

function instantaneo(): Set<string> {
  return vistos;
}

/**
 * `true` quando o painel deve exibir o selo: ainda está dentro do prazo de
 * novidade e esta pessoa nunca o abriu.
 *
 * O prazo evita que um painel de dois anos atrás continue "novo" para quem
 * chega hoje. `novoAte` é uma data ISO (`AAAA-MM-DD`).
 */
export function useEhNovidade(id: string, novoAte: string | undefined, agora: number = Date.now()): boolean {
  const jaVistos = useSyncExternalStore(assinar, instantaneo, instantaneo);
  if (!novoAte) return false;
  const prazo = Date.parse(`${novoAte}T23:59:59Z`);
  if (!Number.isFinite(prazo) || agora > prazo) return false;
  return !jaVistos.has(id);
}

/** Só para os testes. */
export function resetNovidade(): void {
  vistos = new Set();
  try {
    localStorage.removeItem(CHAVE);
  } catch {
    /* sem armazenamento */
  }
  for (const fn of ouvintes) fn();
}
